import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { clstx } from '@payaca/utilities/cvaMerge';
import type { CSSProperties, ReactElement } from 'react';
import type {
  SortableTreeBaseContainer,
  SortableTreeBaseItem,
  SortableTreeContainer,
  SortableTreeNode,
} from './types';

export type SortableTreeItemMeta<
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
> = {
  depth: number;
  isDragging: boolean;
  isInvalidDrop: boolean;
  /** The node's immediate parent container, or `null` at the top level. */
  parent: SortableTreeContainer<TItem, TContainer> | null;
  /**
   * True only for a collapsed container (its descendants are hidden). Always
   * `false` for items and for containers without children.
   */
  collapsed: boolean;
  /**
   * Toggles this container's collapsed state. Provided only for a collapsible
   * container (a container with children, when collapsing is enabled);
   * `undefined` otherwise, so a chevron is rendered only where it makes sense.
   */
  onToggleCollapse?: () => void;
};

export type SortableTreeDropIndicatorMeta<
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
> = {
  /** The node being dragged. */
  node: SortableTreeNode<TItem, TContainer>;
  /** The depth the node would land at. */
  depth: number;
  /** True when the node can't be dropped at this position. */
  isInvalidDrop: boolean;
  /** A valid shallower depth exists at this slot (drag left to reach it). */
  canOutdent: boolean;
  /** A valid deeper depth exists at this slot (drag right to reach it). */
  canIndent: boolean;
};

export type SortableTreeItemProps<
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
> = {
  node: SortableTreeNode<TItem, TContainer>;
  /** The depth to render at — the projected depth while this row is the active one. */
  depth: number;
  indentationWidth: number;
  /** True only for the active row when the current drop position is illegal. */
  isInvalidDrop: boolean;
  canOutdent: boolean;
  canIndent: boolean;
  /** The node's immediate parent container, or `null` at the top level. */
  parent: SortableTreeContainer<TItem, TContainer> | null;
  /** True only for a collapsed container; `false` for items / childless containers. */
  collapsed: boolean;
  /** Toggles this container's collapsed state; `undefined` when the row isn't collapsible. */
  onToggleCollapse?: () => void;
  renderItem: (
    node: SortableTreeNode<TItem, TContainer>,
    meta: SortableTreeItemMeta<TItem, TContainer>
  ) => ReactElement;
  renderDropIndicator: (
    meta: SortableTreeDropIndicatorMeta<TItem, TContainer>
  ) => ReactElement;
};

/**
 * One row of a {@link SortableTree}. Wraps the consumer's `renderItem` output in
 * an indentation wrapper that is the sortable node; the actual drag is initiated
 * by a `DragHandle` rendered inside `renderItem` (handle mode). While dragging,
 * the active row renders `renderDropIndicator` at its projected depth instead.
 */
const SortableTreeItem = <
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
>(
  props: SortableTreeItemProps<TItem, TContainer>
) => {
  const {
    node,
    depth,
    indentationWidth,
    isInvalidDrop,
    canOutdent,
    canIndent,
    parent,
    collapsed,
    onToggleCollapse,
    renderItem,
    renderDropIndicator,
  } = props;

  const { setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
    // Disable dnd-kit's layout FLIP during/just after a drag so it doesn't fight
    // verticalListSortingStrategy's slide; allow it otherwise (add/remove/commit).
    animateLayoutChanges: ({ isSorting, wasDragging }) =>
      !(isSorting || wasDragging),
  });

  const style: CSSProperties = {
    paddingLeft: depth * indentationWidth,
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
  };

  return (
    <div
      ref={setNodeRef}
      role="treeitem"
      aria-level={depth + 1}
      style={style}
      {...(isDragging ? { 'data-dragging': true } : {})}
      {...(isInvalidDrop ? { 'data-invalid-drop': true } : {})}
      {...(onToggleCollapse ? { 'aria-expanded': !collapsed } : {})}
    >
      {isDragging &&
        renderDropIndicator({
          node,
          depth,
          isInvalidDrop,
          canOutdent,
          canIndent,
        })}
      {/* Hidden rather than unmounted while dragging, so a keyboard drag keeps
          focus on the row's DragHandle. */}
      <div className={clstx(isDragging && 'h-0 overflow-hidden opacity-0')}>
        {renderItem(node, {
          depth,
          isDragging,
          isInvalidDrop,
          parent,
          collapsed,
          onToggleCollapse,
        })}
      </div>
    </div>
  );
};

export default SortableTreeItem;
